import styles from './login-form.module.css';
import firebase from '@/utils/firebase';
import FormGroup from '@/components/forms/form-group';
import FormGroupPrefix from '@/components/forms/form-group-prefix';
import FormField from '@/components/forms/form-field';
import FormPhoneField from '@/components/forms/form-phone-field';
import FormError from '@/components/forms/form-error';
import Button from '@/components/button';
import axios from '@/utils/axios';

import {Component} from 'react';
import {withAuth} from '@/contexts/auth-context';
import {FormattedMessage, injectIntl} from 'react-intl';
import {FiLock} from 'react-icons/fi';
import {withRouter} from 'next/router';
import {parsePhoneNumber} from 'react-phone-number-input';

import type {WithAuthProps} from '@/contexts/auth-context';
import type {WrappedComponentProps as IntlProps} from 'react-intl/lib';
import type {ChangeEvent} from 'react';
import type {WithRouterProps} from 'next/dist/client/with-router';

type Props = IntlProps & WithAuthProps & WithRouterProps;

interface State {
  readonly otpSent: boolean;
  readonly phone_number: string;
  readonly phoneNumberExists: boolean;
  readonly password: string;
  readonly error: string;
  readonly loading: boolean;
}

class PhoneNumberLoginForm extends Component<Props, State> {
  private applicationVerifier!: firebase.auth.RecaptchaVerifier;
  private confirmationResult!: firebase.auth.ConfirmationResult;
  public readonly state: Readonly<State> = {
    otpSent: false,
    phone_number: '',
    phoneNumberExists: false,
    password: '',
    error: '',
    loading: false,
  };

  public componentDidMount(): void {
    this.applicationVerifier = new firebase.auth.RecaptchaVerifier('recaptcha');
  }

  public render(): JSX.Element {
    const {formatMessage: t} = this.props.intl;
    const {otpSent, phone_number, password, error, loading} = this.state;
    return (
      <div>
        <FormGroup invalid={false}>
          <FormPhoneField
            type="tel"
            id="phone_number"
            name="phone_number"
            // placeholder={t({id: 'words.phone_number'})}
            placeholder={'+96650123456789'}
            value={phone_number}
            onChange={this.handleFieldChanged}
          />
        </FormGroup>
        {!otpSent && !error && <div id="recaptcha" className="recaptcha" />}
        {otpSent && (
          <FormGroup invalid={false}>
            <FormGroupPrefix>
              <FiLock />
            </FormGroupPrefix>
            <FormField
              type="text"
              id="password"
              name="password"
              placeholder={t({id: 'words.otp'})}
              value={password}
              onChange={this.handleFieldChanged}
            />
          </FormGroup>
        )}
        <FormError>{error}</FormError>
        <div className={styles.submit_button_container}>
          <Button fullWidth onClick={this.handleSubmit} disabled={loading}>
            {loading ? (
              <FormattedMessage id="words.loading" />
            ) : otpSent ? (
              <FormattedMessage id="words.login" />
            ) : (
              <FormattedMessage id="words.send_otp" />
            )}
          </Button>
        </div>
      </div>
    );
  }

  /** @internal */
  private handleFieldChanged = ({target}: ChangeEvent<HTMLInputElement>) => {
    this.setState({[target.id]: target.value} as any);
  };

  /** @internal */
  private handleSubmit = async () => {
    const {formatMessage: t} = this.props.intl;
    try {
      const {phoneNumberExists} = this.state;
      if (!phoneNumberExists) {
        if (!(await this.handleCheckPhoneNumberExists())) {
          return;
        }
      }

      const {otpSent, phone_number, password} = this.state;
      if (!otpSent) {
        this.setState({loading: true, error: ''});
        this.confirmationResult = await firebase.auth().signInWithPhoneNumber(phone_number, this.applicationVerifier);
        return this.setState({otpSent: true, error: '', loading: false});
      }

      if (otpSent && !password) {
        return this.setState({error: t({id: 'errors.invalid_otp'})});
      }
      this.setState({loading: true, error: ''});
      const {user} = await this.confirmationResult.confirm(password);
      if (user && user.phoneNumber && user.uid) {
        await this.props.session.getUserByPhoneNumberAndUid(user.phoneNumber, user.uid);
      }
    } catch (e) {
      if (e.code === 'auth/invalid-verification-code') {
        return this.setState({
          error: t({id: 'errors.invalid_otp'}),
          loading: false,
        });
      } else if (e.code === 'auth/too-many-requests') {
        return this.setState({
          error: t({id: 'errors.too_many_otp_requests'}),
          loading: false,
        });
      }
      this.setState({
        error: t({id: 'errors.unable_to_confirm_phone_number'}),
        loading: false,
      });
    }
  };

  /** @internal */
  private handleCheckPhoneNumberExists = async () => {
    const {formatMessage: t} = this.props.intl;
    try {
      const {phone_number} = this.state;
      const parsedPhoneNumber = parsePhoneNumber(phone_number);
      if (!parsedPhoneNumber) {
        this.setState({
          error: t({id: 'errors.invalid_phone_number'}),
          phoneNumberExists: false,
        });
        return false;
      }
      await axios.get(`/auth/phone-number-exists/${phone_number}`);
      this.setState({phoneNumberExists: true});
      return true;
    } catch (e) {
      this.setState({
        error: t({id: 'errors.phone_number_not_exists'}),
        phoneNumberExists: false,
      });
      return false;
    }
  };
}

export default withRouter(withAuth(injectIntl(PhoneNumberLoginForm)));
